import { TabsWithRoutes } from "@components/common/tabs/tabs-with-routes";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { ForbiddenPage } from "@pages/forbidden-page";
import { TeamDetailsButton } from "@pages/members/teams/team-details-button";
import { TeamMembers } from "@pages/members/teams/team-members";
import { NotFoundPage } from "@pages/not-found-page";
import { MemberOverviewPage } from "@pages/profile/member-overview-page";
import { TeamTabs } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  fetchingTeamsFlagsSelector,
  getTeamByIdSelector,
  selectedTeamAsMemberSelector,
} from "@store/teams/teams-selector";
import { resetTeamState } from "@store/teams/teams-slice";
import {
  fetchTeamAsMember,
  fetchTeamDetails,
} from "@store/teams/teams-slice-thunk";
import { RequiredRoleCompanyLevelName } from "@utils/access-control/company/company-access-control-types";
import { OpenTabEvents } from "@utils/track-event/track-event-list";
import { useEffect } from "react";

/** Renders the team page */
export function TeamPage(): JSX.Element {
  const { teamId, teamTabs } = useAppParams();
  const { canViewTeams, canEditTeams } = useHasUserValidRoleCompanyLevel();
  const selectedTeam = useAppSelector(getTeamByIdSelector(teamId));
  const selectedTeamAsMember = useAppSelector(selectedTeamAsMemberSelector);
  const dispatch = useAppDispatch();
  const { status } = useAppSelector(fetchingTeamsFlagsSelector);

  /** Fetches the required data to display the team details */
  useEffect(() => {
    if (teamId) {
      void dispatch(fetchTeamDetails({ teamId }));

      void dispatch(fetchTeamAsMember({ teamId }));
    }

    return () => {
      dispatch(resetTeamState());
    };
  }, [dispatch, teamId]);

  if (!canViewTeams) {
    return <ForbiddenPage />;
  }

  if (!teamTabs || !Object.values(TeamTabs).includes(teamTabs)) {
    return <NotFoundPage />;
  }

  return (
    <TabsWithRoutes
      selectedTab={teamTabs}
      trackingEventPageName={OpenTabEvents.openTeamTab}
      requiredAttribute={selectedTeam}
      tabs={[
        {
          label: "Members",
          route: TeamTabs.members,
          content: (team) => <TeamMembers team={team} />,
          loadingContent: <div />,
          requiredRoleCompanyLevel: RequiredRoleCompanyLevelName.canViewTeams,
          actionButtons: canEditTeams ? <TeamDetailsButton /> : undefined,
        },
        {
          label: "Groups & Projects",
          route: TeamTabs.projects,
          content: () => (
            <MemberOverviewPage
              member={selectedTeamAsMember}
              isLoading={status === "pending"}
              subjectType="team"
            />
          ),
          loadingContent: (
            <MemberOverviewPage
              member={null}
              isLoading={true}
              subjectType="team"
            />
          ),
        },
      ]}
    />
  );
}
